'use client'

import Link from 'next/link'
import { useAuth } from '../contexts/AuthContext'

export default function HowItWorksPage() {
  const { user } = useAuth()
  
  return (
    <div className="min-h-screen bg-cream text-ink">
      {/* Navbar */}
      <nav className="sticky top-0 z-50 border-b border-ink/[0.06] bg-cream/85 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link href="/" className="font-serif italic font-semibold text-2xl text-ink tracking-tight">
            Deeliver
          </Link>
          <div className="hidden md:flex items-center gap-6 lg:gap-8">
            <Link href="/about" className="text-ink-soft hover:text-ink transition text-sm font-medium flex items-center min-h-[44px]">
              About
            </Link>
            <Link href="/how-it-works" className="text-ink-soft hover:text-ink transition text-sm font-medium flex items-center min-h-[44px]">
              How it works
            </Link>
            <Link href="/faq" className="text-ink-soft hover:text-ink transition text-sm font-medium flex items-center min-h-[44px]">
              FAQ
            </Link>
            <Link href="/register?type=driver" className="text-ink-soft hover:text-ink transition text-sm font-medium flex items-center min-h-[44px]">
              Become a driver
            </Link>
            {user ? (
              <Link 
                href={
                  user.type === 'student' ? '/student/orders' :
                  user.type === 'parent' ? '/rider/new-order' :
                  user.type === 'driver' ? '/driver/schedule' :
                  user.type === 'admin' ? '/admin' : '/login'
                }
                className="text-ink-soft hover:text-ink transition text-sm font-medium flex items-center min-h-[44px]"
              >
                Dashboard
              </Link>
            ) : null}
            <Link 
              href="/rider/new-order" 
              className="bg-ember text-white px-4 sm:px-5 py-2 sm:py-2.5 rounded-full text-sm font-semibold hover:bg-ember-hover transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-ember focus:ring-offset-2 focus:ring-offset-cream min-h-[44px] flex items-center justify-center"
            >
              Start an order
            </Link>
          </div>
          <div className="md:hidden">
            <Link 
              href="/rider/new-order" 
              className="bg-ember text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-ember-hover transition-colors"
            >
              Order
            </Link>
          </div>
        </div>
      </nav>
      <div className="max-w-4xl mx-auto px-8 py-16">
        <div className="mb-8">
          <h1 className="font-serif text-4xl sm:text-5xl font-semibold tracking-tight text-ink mb-3">How It Works</h1>
          <p className="text-ink-soft">Learn how to use Deeliver</p>
        </div>
        
        <div className="space-y-12">
          {/* For Parents */}
          <section>
            <h2 className="font-serif text-3xl font-semibold tracking-tight text-ink mb-6">For Parents</h2>
            <div className="space-y-6">
              <div className="bg-white border border-cream-deep rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-ember text-white rounded-full flex items-center justify-center font-bold">
                    1
                  </div>
                  <div>
                    <h3 className="font-display text-xl font-semibold text-ink mb-2">Create Your Account</h3>
                    <p className="text-gray-700">
                      Sign up as a parent and provide your contact information along with your student's details 
                      (name, email, phone, and campus address).
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-cream-deep rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-ember text-white rounded-full flex items-center justify-center font-bold">
                    2
                  </div>
                  <div>
                    <h3 className="font-display text-xl font-semibold text-ink mb-2">Select Pickup & Dropoff Locations</h3>
                    <p className="text-gray-700">
                      Choose a convenient pickup location in your area and select a campus meetup spot where
                      your student will meet the driver in person. Popular meetup locations include outside Jester West
                      and 26 West Apartments. Note: deliveries are not made to dorm front desks or mailrooms.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-cream-deep rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-ember text-white rounded-full flex items-center justify-center font-bold">
                    3
                  </div>
                  <div>
                    <h3 className="font-display text-xl font-semibold text-ink mb-2">Choose a Driver</h3>
                    <p className="text-gray-700">
                      Browse available drivers and select one that matches your schedule. You can see driver details, 
                      vehicle information, and their usual routes.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-cream-deep rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-ember text-white rounded-full flex items-center justify-center font-bold">
                    4
                  </div>
                  <div>
                    <h3 className="font-display text-xl font-semibold text-ink mb-2">Schedule Your Delivery</h3>
                    <p className="text-gray-700">
                      Pick your preferred pickup date and time window. Package your meal in a sealed, insulated bag
                      labeled with your student&apos;s name, phone number, preparation date, and a safe-handling statement
                      for any perishable items. Use ice packs or heat packs to maintain safe temperatures during transit.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-cream-deep rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-ember text-white rounded-full flex items-center justify-center font-bold">
                    5
                  </div>
                  <div>
                    <h3 className="font-display text-xl font-semibold text-ink mb-2">Drop Off & Track</h3>
                    <p className="text-gray-700">
                      On the scheduled day, drop off your meal at the pickup location. You'll receive real-time updates 
                      as the driver picks up your order and delivers it to campus. Track the delivery live!
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* For Drivers */}
          <section>
            <h2 className="font-serif text-3xl font-semibold tracking-tight text-ink mb-6">For Drivers</h2>
            <div className="space-y-6">
              <div className="bg-white border border-cream-deep rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-ember text-white rounded-full flex items-center justify-center font-bold">
                    1
                  </div>
                  <div>
                    <h3 className="font-display text-xl font-semibold text-ink mb-2">Register as a Driver</h3>
                    <p className="text-gray-700">
                      Create your driver profile with your vehicle information, license, and insurance. Set up your 
                      regular routes and availability schedule.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-cream-deep rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-ember text-white rounded-full flex items-center justify-center font-bold">
                    2
                  </div>
                  <div>
                    <h3 className="font-display text-xl font-semibold text-ink mb-2">Connect Your Bank Account</h3>
                    <p className="text-gray-700">
                      Securely connect your bank account through Stripe to receive payments. This is required before 
                      you can accept orders.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-cream-deep rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-ember text-white rounded-full flex items-center justify-center font-bold">
                    3
                  </div>
                  <div>
                    <h3 className="font-display text-xl font-semibold text-ink mb-2">Accept Orders</h3>
                    <p className="text-gray-700">
                      Browse available orders that match your route and schedule. Accept orders that fit your capacity 
                      and availability.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-cream-deep rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-ember text-white rounded-full flex items-center justify-center font-bold">
                    4
                  </div>
                  <div>
                    <h3 className="font-display text-xl font-semibold text-ink mb-2">Pick Up & Deliver</h3>
                    <p className="text-gray-700">
                      On the scheduled day, pick up sealed meals from the designated pickup location, verify packaging
                      is intact, and deliver directly to the student at the agreed campus meetup spot. Update your
                      status as you go so parents and students can track progress.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-cream-deep rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-ember text-white rounded-full flex items-center justify-center font-bold">
                    5
                  </div>
                  <div>
                    <h3 className="font-display text-xl font-semibold text-ink mb-2">Get Paid</h3>
                    <p className="text-gray-700">
                      Once delivery is confirmed, you'll receive 70% of the delivery fee (70/30 split). Payments are automatically 
                      transferred to your connected bank account.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* For Students */}
          <section>
            <h2 className="font-serif text-3xl font-semibold tracking-tight text-ink mb-6">For Students</h2>
            <div className="space-y-6">
              <div className="bg-white border border-cream-deep rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-ember text-white rounded-full flex items-center justify-center font-bold">
                    1
                  </div>
                  <div>
                    <h3 className="font-display text-xl font-semibold text-ink mb-2">Receive Order Notification</h3>
                    <p className="text-gray-700">
                      When your parent places an order, you'll receive a notification with order details and a unique 
                      confirmation code.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-cream-deep rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-ember text-white rounded-full flex items-center justify-center font-bold">
                    2
                  </div>
                  <div>
                    <h3 className="font-display text-xl font-semibold text-ink mb-2">Track Your Delivery</h3>
                    <p className="text-gray-700">
                      Watch your meal's journey in real-time. See when the driver picks it up, when they're en route, 
                      and get notified when they're nearby.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-cream-deep rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-ember text-white rounded-full flex items-center justify-center font-bold">
                    3
                  </div>
                  <div>
                    <h3 className="font-display text-xl font-semibold text-ink mb-2">Meet Driver & Confirm</h3>
                    <p className="text-gray-700">
                      Meet the driver at your designated campus meetup spot and receive your meal in person. Confirm
                      delivery using the confirmation code — this completes the order and releases payment to the driver.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>

        <div className="mt-12">
          <Link 
            href="/"
            className="inline-block bg-ember text-white px-6 py-3 rounded-full font-semibold hover:bg-ember-hover transition-colors min-h-[44px]"
          >
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  )
}
